<script lang="ts" setup="">
import { tpgIconAddReaction } from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import {
  computed,
  inject,
  ref,
  toRefs,
} from 'vue';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import {
  bgPrimary,
  bgSeparatorNoTransparency,
  borderSeparatorNoTransparency,
  NoraEmojiPicker,
  useConfigStore,
} from '@nora-monorepo/ui-shared';

import MessageOptions from '#nora-chat-ui/components/open-conversations/messages/message-options/MessageOptions.vue';
import type { MessageState } from '#nora-chat-ui/states';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

interface MessageReactionsProps {
  messageState: MessageState;
  maxReactions: number;
}

const props = defineProps<MessageReactionsProps>();
const emit = defineEmits([
  'messageActionsMouseLeave',
  'messageActionsMouseEnter',
  'emojiClicked',
]);

const { messageState } = toRefs(props);

const userStore = useChatUserStore();

const config = useConfigStore();
const { appConfig } = storeToRefs(config);

const conversationMessageStore = useConversationMessageStore();

const isMyMessage = computed(
  () => messageState.value.message.sentBy.id === userStore.user?.id,
);

const optionMenuOpen = ref(false);
const emojiPickerToggle = ref<HTMLElement>();

const handleOptionMenuToggled = inject<(isOpen: boolean) => void>(
  'handleOptionMenuToggled',
);
const emojiPickerOpen = ref(false);

const defaultReactionEmojisFromConfig = computed<string[]>(() => {
  return appConfig.value?.chatUi.reactionEmojis ?? [];
});

function setEmojiPickerOpen(isOpen: boolean) {
  emojiPickerOpen.value = isOpen;

  if (handleOptionMenuToggled) {
    handleOptionMenuToggled(isOpen);
  }
}

function onOptionMenuOpened() {
  emit('messageActionsMouseEnter');
  optionMenuOpen.value = true;
}

function onOptionMenuClosed() {
  emit('messageActionsMouseLeave');
  optionMenuOpen.value = false;
}

function onMessageActionsMouseLeave() {
  if (!optionMenuOpen.value) {
    emit('messageActionsMouseLeave');
  }
}

function handleReactionClick(emoji: string) {
  conversationMessageStore.sendMessageReaction(props.messageState, emoji);
  optionMenuOpen.value = false;
  emit('emojiClicked');
}

function onItemClicked() {
  emit('messageActionsMouseLeave');
}

function toggleEmojiPicker() {
  if (emojiPickerOpen.value) {
    onHideEmojiPanel();
  }
  else {
    onShowEmojiPanel();
  }
}

function onShowEmojiPanel() {
  emit('messageActionsMouseEnter');
  setEmojiPickerOpen(true);
}

function onHideEmojiPanel() {
  emit('messageActionsMouseLeave');
  setEmojiPickerOpen(false);
}

function onEmojiClicked(emoji: string) {
  conversationMessageStore.sendMessageReaction(props.messageState, emoji);
  onHideEmojiPanel();
}

function closeEmojiPicker() {
  onHideEmojiPanel();
}
</script>

<template>
  <span>
    <NoraEmojiPicker
      v-if="emojiPickerOpen && emojiPickerToggle"
      :toggle-ref="emojiPickerToggle"
      @emoji-clicked="(emoji: string) => onEmojiClicked(emoji)"
      @close="closeEmojiPicker"
    />

    <div
      class="flex absolute shadow-md border rounded-lg bottom-0 items-center z-50 h-12 max-w-[90vw] md:max-w-[420px] overflow-x-auto"
      :class="[bgPrimary, borderSeparatorNoTransparency]"
      :style="isMyMessage ? { right: '2px' } : { left: '2px' }"
      @mouseleave="onMessageActionsMouseLeave"
    >
      <GrammarQuaternaryButton
        v-for="emoji of defaultReactionEmojisFromConfig"
        :key="emoji"
        :label="emoji"
        style="--buttonQuaternaryTypographyLabelRegularDefault: var(--typographyLabelLgNormal)"
        @click="() => handleReactionClick(emoji)"
      />
      <GrammarQuaternaryButton
        ref="emojiPickerToggle"
        :icon-template="tpgIconAddReaction"
        @click="toggleEmojiPicker"
      />
      <span
        class="mx-1 h-5/6"
        :class="bgSeparatorNoTransparency"
        style="width: 1px"
      />
      <MessageOptions
        :message-state="messageState"
        @option-menu-opened="() => onOptionMenuOpened()"
        @option-menu-closed="() => onOptionMenuClosed()"
        @on-item-clicked="onItemClicked"
      />
    </div>
  </span>
</template>

<style scoped></style>
